import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cmWorkflowClient, type CreateCMWorkflowRequest } from '../../../infrastructure/CMWorkflowClient';
import type { CMWorkflowItem } from '../../../models/CatalogManagement';

export const useCMWorkflowByGPI = (gpi: string, enabled = true) => {
  return useQuery<CMWorkflowItem[], Error>({
    queryKey: ['cmWorkflow', gpi],
    queryFn: async () => {
      try {
        return await cmWorkflowClient.getCMWorkflowByParams({ gpi });
      } catch (error: any) {
        // 404 is expected when CM workflow doesn't exist yet (first start)
        if (error?.response?.status === 404) {
          console.info(`ℹ️ No CM workflow found for GPI ${gpi} - this is normal for first start`);
          return []; // Return empty array instead of throwing error
        }
        throw error; // Re-throw other errors
      }
    },
    enabled: enabled && Boolean(gpi),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
  });
};

export const useCreateCMWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation<CMWorkflowItem, Error, CreateCMWorkflowRequest>({
    mutationFn: (request: CreateCMWorkflowRequest) => cmWorkflowClient.createCMWorkflow(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cmWorkflow', data.gpi] });
    },
  });
};
